import { useState } from "react";
import { Plus, PackageCheck, Pencil, Ban, Printer, History } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { StatusBadge, WarehouseBadge } from "@/components/Badges";
import { PurchaseOrderDialog } from "@/components/OrderDialogs";
import { ReceiveDialog } from "@/components/ReceiveDialog";
import { Link } from "react-router-dom";
import { todayStr } from "@/lib/format";
import { useLoad } from "@/hooks/useData";
import { usePref } from "@/hooks/usePref";
import { api, errMsg, notifyStockChanged } from "@/lib/api";
import { dateLabel } from "@/lib/format";

export default function PurchaseOrders() {
  const [status, setStatus] = usePref("purchase_orders-status", "all");
  const { data } = useLoad(() => api.get("/purchase-orders", { params: status === "all" ? {} : { status } }).then((r) => r.data), [status]);
  const [edit, setEdit] = useState({ open: false, order: null });
  const [receive, setReceive] = useState({ open: false, order: null });
  const cancel = async (o) => {
    if (!window.confirm("Cancel this order?")) return;
    try { await api.post(`/purchase-orders/${o.id}/cancel`); toast.success("Order cancelled"); notifyStockChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader title="Purchase / Manufacturing Orders" subtitle="Raise orders for production or suppliers; stock is added (IN) when received.">
        <SimpleSelect value={status} onChange={setStatus} testid="po-status-filter"
          options={[["all", "All statuses"], ["pending", "Pending"], ["partial", "Partially received"], ["received", "Received"], ["cancelled", "Cancelled"]]} />
        <Button size="sm" onClick={() => setEdit({ open: true, order: null })} data-testid="new-purchase-order-btn"><Plus className="h-4 w-4" /> New order</Button>
      </PageHeader>
      {!data ? <Loading /> : (
        <div className="panel overflow-auto">
          <table className="data-table" data-testid="purchase-orders-table">
            <thead><tr><th>Order</th><th>Type</th><th>Date</th><th>Supplier / Vendor</th><th>Items (received / ordered)</th><th>WH</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {data.map((o) => (
                <tr key={o.id} data-testid={`po-row-${o.order_no}`}>
                  <td className="font-mono font-bold text-primary">{o.order_no}</td>
                  <td className="text-xs uppercase tracking-wider">{o.kind}</td>
                  <td className="text-xs">{dateLabel(o.order_date)}{o.expected_date && <div className="text-muted-foreground">due {dateLabel(o.expected_date)}</div>}</td>
                  <td>{o.supplier || "—"}</td>
                  <td className="wrap-cell text-xs min-w-[280px] max-w-[520px]">{o.items.map((i) => <div key={i.product_id} className="product-name py-0.5"><span className="font-mono">{i.received || 0}/{i.qty}</span> {i.name}</div>)}</td>
                  <td><WarehouseBadge value={o.warehouse} /></td>
                  <td><StatusBadge value={o.status} />{o.expected_date && o.expected_date < todayStr() && ["pending", "partial"].includes(o.status) && <div className="text-[10px] font-semibold uppercase text-red-600" data-testid={`po-overdue-${o.order_no}`}>overdue</div>}</td>
                  <td className="whitespace-nowrap">
                    <a href={`/print/po/${o.id}`} target="_blank" rel="noreferrer" title="Print / PDF" className="inline-block p-1.5 text-muted-foreground hover:text-primary" data-testid={`print-po-${o.order_no}`}><Printer className="h-4 w-4" /></a>
                    <Link to={`/po-receipts/po/${o.id}`} title="Receipts (GRNs)" className="inline-block p-1.5 text-muted-foreground hover:text-primary" data-testid={`receipts-po-${o.order_no}`}><History className="h-4 w-4" /></Link>
                    {["pending", "partial"].includes(o.status) && <Button size="sm" variant="outline" onClick={() => setReceive({ open: true, order: o })} data-testid={`receive-btn-${o.order_no}`}><PackageCheck className="h-4 w-4" /> Receive</Button>}
                    {o.status === "pending" && <>
                      <button className="p-1.5 text-muted-foreground hover:text-primary" onClick={() => setEdit({ open: true, order: o })} data-testid={`edit-po-${o.order_no}`}><Pencil className="h-4 w-4" /></button>
                      <button className="p-1.5 text-muted-foreground hover:text-red-400" onClick={() => cancel(o)} data-testid={`cancel-po-${o.order_no}`}><Ban className="h-4 w-4" /></button>
                    </>}
                  </td>
                </tr>
              ))}
              {!data.length && <tr><td colSpan={8} className="py-12 text-center text-muted-foreground">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <PurchaseOrderDialog open={edit.open} order={edit.order} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} />
      <ReceiveDialog open={receive.open} order={receive.order} onOpenChange={(o) => setReceive((s) => ({ ...s, open: o }))} />
    </div>
  );
}
